import consumer from "./consumer"

// Subscriber for the /fulfillment workspace. Receives JSON messages on the
// global "fulfillment_updates" stream and applies row / box-list updates to
// the DOM live so all connected browsers stay in sync without a refresh.
//
// Message shapes:
//   { type: "amtec_rq_item", id, packaging_status, serial_number, rq_box_id }
//   { type: "sf_part",       id, packaging_status }
//   { type: "rq_box_changed", rq_id, action: "created"|"destroyed",
//     boxes: [{ id, box_number, box_type_name, label }] }

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function(c) {
    return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
  });
}

function applyPackagingToRow(rowEl, status) {
  if (!rowEl) return;
  var bgClasses = ['packaging-bg-default','packaging-bg-not_packaged','packaging-bg-packaged','packaging-bg-partially_packaged','packaging-bg-backordered','packaging-bg-pulled'];
  bgClasses.forEach(function(c) { rowEl.classList.remove(c); });
  rowEl.classList.add('packaging-bg-' + (status || 'default'));

  var iconClasses = ['packaging-not_packaged','packaging-packaged','packaging-partially_packaged','packaging-backordered','packaging-pulled'];
  rowEl.querySelectorAll('.packaging-status-btn i.packaging-icon').forEach(function(icon) {
    iconClasses.forEach(function(c) { icon.classList.remove(c); });
    icon.classList.add('packaging-' + (status || 'not_packaged'));
  });
  rowEl.querySelectorAll('.packaging-status-btn').forEach(function(btn) {
    btn.setAttribute('data-status', status || 'not_packaged');
  });
}

function applyAmtecRqItemUpdate(data) {
  var rows = document.querySelectorAll('[data-rq-item-row="' + data.id + '"]');
  rows.forEach(function(row) {
    if (Object.prototype.hasOwnProperty.call(data, 'packaging_status')) {
      applyPackagingToRow(row, data.packaging_status);
    }
    if (Object.prototype.hasOwnProperty.call(data, 'serial_number')) {
      var serialLink = row.querySelector('.serial-number-editable[data-rq-item-id="' + data.id + '"]');
      if (serialLink) {
        serialLink.setAttribute('data-current', data.serial_number || '');
        serialLink.innerHTML = data.serial_number
          ? escapeHtml(data.serial_number)
          : '<i class="fa fa-pencil text-muted"></i>';
      }
    }
    if (Object.prototype.hasOwnProperty.call(data, 'rq_box_id')) {
      var sel = row.querySelector('.box-assignment-select[data-rq-item-id="' + data.id + '"]');
      if (sel) sel.value = (data.rq_box_id != null) ? String(data.rq_box_id) : '';
    }
  });
}

function applySfPartUpdate(data) {
  // sf-part rows are identified by the button's data-sf-part-id, not by the
  // row's data-rq-item-row (which is blank for outside-vendor rows).
  document.querySelectorAll('.sf-part-packaging[data-sf-part-id="' + data.id + '"]').forEach(function(btn) {
    var row = btn.closest('.product-row');
    if (row && Object.prototype.hasOwnProperty.call(data, 'packaging_status')) {
      applyPackagingToRow(row, data.packaging_status);
    }
  });
}

function rebuildSelectOptions(sel, boxes) {
  var currentVal = sel.value;
  var optsHtml = '<option value="">--</option>' + boxes.map(function(b) {
    return '<option value="' + b.id + '">' + escapeHtml(b.box_number + ': ' + b.box_type_name) + '</option>';
  }).join('');
  sel.innerHTML = optsHtml;
  var stillPresent = boxes.some(function(b) { return String(b.id) === currentVal; });
  sel.value = stillPresent ? currentVal : '';
}

function applyRqBoxChanged(data) {
  var rqId = String(data.rq_id);
  var boxes = Array.isArray(data.boxes) ? data.boxes : [];

  // 1) Rebuild every existing assignment select scoped to this RQ.
  document.querySelectorAll('.box-assignment-select[data-rq-id="' + rqId + '"]').forEach(function(sel) {
    rebuildSelectOptions(sel, boxes);
  });

  // 2) If the RQ now has no boxes, drop the selects entirely (matches existing
  //    local-update behavior: a row with no boxes shows no dropdown).
  if (boxes.length === 0) {
    document.querySelectorAll('.box-assignment-select[data-rq-id="' + rqId + '"]').forEach(function(sel) {
      sel.remove();
    });
  }

  // 3) When boxes were added and a row of this RQ has no select yet, build one.
  if (boxes.length > 0) {
    document.querySelectorAll('.fulfillment-product-row[data-rq-id="' + rqId + '"]').forEach(function(row) {
      var col = row.querySelector('.fulfillment-col-box');
      if (!col) return;
      if (col.querySelector('.box-assignment-select')) return;
      var rqItemId = row.getAttribute('data-rq-item-row');
      if (!rqItemId) return;
      var sel = document.createElement('select');
      sel.className = 'box-assignment-select';
      sel.setAttribute('data-rq-item-id', rqItemId);
      sel.setAttribute('data-rq-id', rqId);
      rebuildSelectOptions(sel, boxes);
      col.innerHTML = '';
      col.appendChild(sel);
    });
  }

  // 4) Reconcile the visible box tags in the .fulfillment-boxes-section so the
  //    "Add box…" UI reflects the broadcast state.
  var section = document.querySelector('.fulfillment-boxes-section[data-rq-id="' + rqId + '"]');
  if (!section) return;

  if (data.action === 'destroyed') {
    var keepIds = new Set(boxes.map(function(b) { return String(b.id); }));
    section.querySelectorAll('.fulfillment-box-tag').forEach(function(tag) {
      if (!keepIds.has(String(tag.getAttribute('data-box-id')))) tag.remove();
    });
  } else if (data.action === 'created') {
    var existingIds = new Set(Array.from(section.querySelectorAll('.fulfillment-box-tag')).map(function(t) {
      return String(t.getAttribute('data-box-id'));
    }));
    var form = section.querySelector('.fulfillment-add-box-form');
    boxes.forEach(function(b) {
      if (existingIds.has(String(b.id))) return;
      var tag = document.createElement('span');
      tag.className = 'fulfillment-box-tag';
      tag.setAttribute('data-box-id', b.id);
      tag.innerHTML = escapeHtml('Box ' + b.box_number + ': ' + b.box_type_name) +
        ' <a href="#" class="remove-box" data-box-id="' + b.id + '" title="Remove box">&times;</a>';
      if (form && form.parentNode) form.parentNode.insertBefore(tag, form);
      else section.appendChild(tag);
    });
  }
}

function pageVendorIds() {
  var el = document.querySelector('[data-fulfillment-page]');
  if (!el) return [];
  try { return JSON.parse(el.getAttribute('data-vendor-ids') || '[]'); } catch (_) { return []; }
}

function pageIsInternal() {
  var el = document.querySelector('[data-fulfillment-page]');
  return !!(el && el.getAttribute('data-is-internal') === 'true');
}

function pageD365VendorIds() {
  var el = document.querySelector('[data-fulfillment-page]');
  if (!el) return [];
  try { return JSON.parse(el.getAttribute('data-d365-vendor-ids') || '[]'); } catch (_) { return []; }
}

function applyOrderAdded(data) {
  // Vendor / role gating mirrors the controller-side filtering so a card
  // never lands somewhere it wouldn't have on a fresh page load.
  if (data.source === 'd365') {
    if (!pageIsInternal()) return;
    var allowedD365 = pageD365VendorIds();
    if (allowedD365.indexOf(data.vendor_value) === -1) return;
  } else if (data.source === 'sf') {
    var allowed = pageVendorIds();
    var match = (data.vendor_ids || []).some(function (v) { return allowed.indexOf(v) !== -1; });
    if (!match) return;
  } else {
    return;
  }

  if (document.getElementById('fulfillment-order-' + data.order_id)) return; // dedupe

  var url = '/orders/' + data.order_id + '/fulfillment_card?source=' + encodeURIComponent(data.source);
  fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
    .then(function (r) { if (!r.ok) throw new Error('fetch ' + r.status); return r.text(); })
    .then(function (html) {
      if (!html || !html.trim()) return;
      var container = document.getElementById('fulfillment-cards');
      if (!container) return;
      var wrapper = document.createElement('div');
      wrapper.innerHTML = html.trim();
      var card = wrapper.firstElementChild;
      if (!card) return;
      if (document.getElementById(card.id)) return; // dedupe again post-fetch
      container.insertBefore(card, container.firstChild);

      // Bump totals.
      var total = document.getElementById('fulfillment-total');
      if (total) total.textContent = String((parseInt(total.textContent, 10) || 0) + 1);
      var totalNav = document.getElementById('fulfillment-total-nav');
      if (totalNav) totalNav.textContent = String((parseInt(totalNav.textContent, 10) || 0) + 1);

      // Re-run the page's filter so #fulfillment-showing reflects whether the
      // new card matches the active filter. The page exposes a global hook.
      if (typeof window.applyFulfillmentFilter === 'function') {
        try { window.applyFulfillmentFilter(); } catch (_) { /* swallow */ }
      }
    })
    .catch(function (err) {
      console.warn('FulfillmentChannel order_added fetch failed', err);
    });
}

function applyProductHoldChanged(data) {
  // Find every product row tied to this product_type / oes_part_number and
  // toggle the badge + row class accordingly. Per-row packaging bg/icon
  // changes ride on the per-rq_item broadcasts the model fires alongside
  // this one.
  var selectors = [];
  if (data.product_type_id != null) selectors.push('[data-product-type-id="' + data.product_type_id + '"]');
  if (data.oes_part_number)         selectors.push('[data-part-number="' + data.oes_part_number + '"]');
  if (!selectors.length) return;

  var rows = document.querySelectorAll(selectors.join(','));
  rows.forEach(function(row) {
    if (data.held) {
      row.classList.add('product-on-hold');
      var nameCol = row.querySelector('.fulfillment-col-name, .detail-location, .product-details');
      if (nameCol && !row.querySelector('.fulfillment-hold-badge')) {
        var span = document.createElement('span');
        span.className = 'badge badge-danger fulfillment-hold-badge ml-1';
        span.title = data.held_reason || 'Shipping is on hold for this product';
        span.innerHTML = '<i class="fa fa-pause-circle"></i> ON HOLD';
        nameCol.appendChild(span);
      } else {
        // Update tooltip on the existing badge with the latest reason.
        var existing = row.querySelector('.fulfillment-hold-badge');
        if (existing) existing.title = data.held_reason || existing.title;
      }
    } else {
      row.classList.remove('product-on-hold');
      row.querySelectorAll('.fulfillment-hold-badge').forEach(function(b) { b.remove(); });
    }
  });
}

function handleFulfillmentLoad() {
  if (!document.querySelector('[data-fulfillment-page]')) return;

  if (window.fulfillmentSubscription) {
    window.fulfillmentSubscription.unsubscribe();
    window.fulfillmentSubscription = null;
  }

  window.fulfillmentSubscription = consumer.subscriptions.create("FulfillmentChannel", {
    connected() {
      // console.log("✓ FulfillmentChannel connected");
    },
    disconnected() {
      // console.log("✗ FulfillmentChannel disconnected");
    },
    received(data) {
      if (!data || !data.type) return;
      try {
        switch (data.type) {
          case 'amtec_rq_item':        applyAmtecRqItemUpdate(data); break;
          case 'sf_part':              applySfPartUpdate(data); break;
          case 'rq_box_changed':       applyRqBoxChanged(data); break;
          case 'order_added':          applyOrderAdded(data); break;
          case 'product_hold_changed': applyProductHoldChanged(data); break;
        }
      } catch (e) {
        console.error('FulfillmentChannel handler error', e, data);
      }
    }
  });
}

function handleFulfillmentBeforeCache() {
  if (window.fulfillmentSubscription) {
    window.fulfillmentSubscription.unsubscribe();
    window.fulfillmentSubscription = null;
  }
}

// Dedupe across module re-evaluations so handlers don't stack on repeat navs.
if (window._fulfillmentLoadHandler) {
  document.removeEventListener('turbolinks:load', window._fulfillmentLoadHandler);
}
if (window._fulfillmentBeforeCacheHandler) {
  document.removeEventListener('turbolinks:before-cache', window._fulfillmentBeforeCacheHandler);
}
window._fulfillmentLoadHandler = handleFulfillmentLoad;
window._fulfillmentBeforeCacheHandler = handleFulfillmentBeforeCache;
document.addEventListener('turbolinks:load', window._fulfillmentLoadHandler);
document.addEventListener('turbolinks:before-cache', window._fulfillmentBeforeCacheHandler);
